import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Empty, Image, Tooltip, Modal, Skeleton, message } from "antd";
import {
    ReloadOutlined,
    ShopOutlined,
    CalendarOutlined,
    UserOutlined,
    EditOutlined,
    BellOutlined,
    VideoCameraOutlined,
    InboxOutlined,
    FireOutlined,
    WarningOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    AppstoreOutlined,
    CheckCircleFilled,
    FieldTimeOutlined,
    ExpandAltOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar as RBar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RTooltip,
    Legend,
} from "recharts";
import axios from "../../utils/axiosDash";

/* ------------------------------------------------------------------
 * NOTE: the table occupancy code (OccupancyCard, TableUsageCard,
 * TableHoursCard and their fetch / KPI pieces) was commented out in the
 * previous version. It is left out of this file to keep it short.
 * Keep it in your old file if you want to bring it back later.
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------
 * CONFIG
 * ------------------------------------------------------------------ */
const STORE_STORAGE_KEY = "selectedStore"; // import yours if it already exists
const ALL_STORES = "All Stores";
const TZ = "UTC"; // database values are UTC and no +5:30 shift is applied
const OVERVIEW = "__overview__";

// one API per section, each called by its own function
const API = {
    cameras: "get-empire-dash-cameras-data",
    alerts: "get-empire-dash-alerts-data", // cashier alerts, writer alerts, storeroom, hygiene
    kabab: "get-empire-dash-kabab-data",
    presence: "get-empire-dash-presence-data", // cashier + writer presence
};
const REFRESH_MS = 60 * 1000;

const C = {
    present: "#34d399",
    absent: "#fb7185",
    partial: "#fbbf24",
    empty: "#e2e8f0",
    sky: "#38bdf8",
    amber: "#fbbf24",
    violet: "#a78bfa",
    orange: "#fb923c",
    teal: "#2dd4bf",
};

// soft light tooltip for all charts
const tipStyle = {
    contentStyle: {
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        boxShadow: "0 8px 24px -12px rgba(15,23,42,0.15)",
        fontSize: 12,
        color: "#334155",
    },
    cursor: { fill: "rgba(148,163,184,0.12)" },
};

// `section` = which API feeds this source (used for per-card loading)
const SOURCES = [
    { key: "cashierAlerts", section: "alerts", label: "Cashier", color: C.sky, icon: <UserOutlined />, timeField: "postgresCreatedAt" },
    { key: "writerAlerts", section: "alerts", label: "Writer", color: C.orange, icon: <EditOutlined />, timeField: "postgresCreatedAt" },
    { key: "storeRoom", section: "alerts", label: "Storeroom", color: C.violet, icon: <InboxOutlined />, timeField: "postgresCreatedAt" },
    { key: "hygiene", section: "alerts", label: "Hygiene", color: C.teal, icon: <SafetyCertificateOutlined />, timeField: "postgresCreatedAt" },
    { key: "kabab", section: "kabab", label: "Kebab fry", color: C.amber, icon: <FireOutlined />, timeField: "postgresCreatedAt" },
];

/* ------------------------------------------------------------------
 * HELPERS
 * ------------------------------------------------------------------ */
const todayStr = () => new Date().toISOString().slice(0, 10);

const fmtTime = (v) =>
    v ? new Date(v).toLocaleTimeString("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit" }) : "-";

const fmtTimeSec = (v) =>
    v
        ? new Date(v).toLocaleTimeString("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit", second: "2-digit" })
        : "-";

const fmtDateTime = (v) =>
    v
        ? new Date(v).toLocaleString("en-GB", {
              timeZone: TZ,
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
          })
        : "-";

const words = (s) =>
    String(s || "")
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/^./, (c) => c.toUpperCase());

const isImgUrl = (v) => typeof v === "string" && /^https?:\/\/.+\.(jpe?g|png|webp|gif)(\?|$)/i.test(v);
const pickImage = (row) => Object.values(row || {}).find(isImgUrl);
const isPresent = (s) => String(s).toUpperCase() === "PRESENT";
const durText = (s) => {
    const n = Math.round(Number(s) || 0);
    if (!n) return "-";
    if (n >= 3600) return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m`;
    return n >= 60 ? `${Math.floor(n / 60)}m ${n % 60}s` : `${n}s`;
};
const minsText = (m) => {
    const n = Math.round(Number(m) || 0);
    if (!n) return "0m";
    return n >= 60 ? `${Math.floor(n / 60)}h ${n % 60}m` : `${n}m`;
};

// alerts = cashier + writer + store room + hygiene (kabab shown separately)
const alertTotal = (d) =>
    (d.cashierAlerts?.count || 0) +
    (d.writerAlerts?.count || 0) +
    (d.storeRoom?.count || 0) +
    (d.hygiene?.count || 0);

/* ---------- hygiene helpers (same rules as the Safety & Hygiene page) ----------
 * A hygiene row is a violation when
 *   - gloveStatus   is "NO"                         -> no gloves
 *   - hairnetStatus is "NO"                         -> no hairnet
 *   - alertType is "duster" (or the old dusterStatus / dusterDetected fields say yes) -> duster detected
 * Empty / null / "Unknown" statuses are NOT violations, they are simply ignored. */
const isNo = (v) => String(v ?? "").trim().toUpperCase() === "NO";
const dusterSeen = (r) => {
    const byType = String(r?.alertType || "").trim().toLowerCase() === "duster";
    const raw = String(r?.dusterStatus ?? r?.dusterDetected ?? "").toUpperCase();
    return byType || ["YES", "TRUE", "1", "VISIBLE", "DETECTED"].includes(raw);
};
// duster is its own kind of alert: a duster row is never also counted as gloves / hairnet.
// A gloves / hairnet frame can have BOTH missing, and then it counts once in each.
const noGloves = (r) => !dusterSeen(r) && isNo(r?.gloveStatus);
const noHairnet = (r) => !dusterSeen(r) && isNo(r?.hairnetStatus);
const isHygViolation = (r) => noGloves(r) || noHairnet(r) || dusterSeen(r);
const hygIssues = (r) =>
    dusterSeen(r)
        ? ["Duster detected"]
        : [noGloves(r) && "No gloves", noHairnet(r) && "No hairnet"].filter(Boolean);

const hygCounts = (rows = []) => ({
    gloves: rows.filter(noGloves).length,
    hairnet: rows.filter(noHairnet).length,
    // same as the hygiene page: one frame with BOTH gloves and hairnet missing
    both: rows.filter((r) => noGloves(r) && noHairnet(r)).length,
    duster: rows.filter(dusterSeen).length,
});

// keep only the violation rows and rebuild count / latest,
// so every number on the dashboard matches the Safety & Hygiene page
const cleanHygiene = (data) => {
    if (!data?.hygiene) return data;
    const rows = (data.hygiene.data || []).filter(isHygViolation);
    return { ...data, hygiene: { ...data.hygiene, data: rows, count: rows.length, latest: rows[0] || null } };
};

/* which sections have finished loading: { cameras: true, ... } (one flag per API) */
const SectionsCtx = createContext({});
const useReady = () => useContext(SectionsCtx);

/* ------------------------------------------------------------------
 * BUILDING BLOCKS
 * ------------------------------------------------------------------ */
const Card = ({ title, icon, extra, children, className = "" }) => (
    <section
        className={`rounded-3xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] sm:p-6 ${className}`}
    >
        <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-3 text-[15px] font-semibold text-slate-800">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-[15px] text-sky-500 ring-1 ring-sky-100">
                    {icon}
                </span>
                {title}
            </h3>
            {extra}
        </div>
        {children}
    </section>
);

// small pulsing placeholder used inside numbers / chips
const Pulse = ({ className = "h-5 w-12" }) => (
    <span className={`inline-block animate-pulse rounded-md bg-slate-200 align-middle ${className}`} />
);

// full card placeholder shown until a section's API has answered
const CardSkeleton = ({ title, icon, rows = 5 }) => (
    <Card title={title} icon={icon}>
        <Skeleton active paragraph={{ rows }} />
    </Card>
);

const Gate = ({ ready, title, icon, rows, children }) =>
    ready ? children : <CardSkeleton title={title} icon={icon} rows={rows} />;

const Kpi = ({ label, value, sub, icon, tint, loading }) => (
    <div className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg ${tint}`}>{icon}</div>

        <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-slate-500">{label}</p>
            <p className="truncate text-xl font-semibold leading-tight text-slate-800">
                {loading ? <Pulse className="h-6 w-14" /> : value}
            </p>
            <p className="break-words text-[11px] leading-4 text-slate-400">
                {loading ? <Pulse className="h-3 w-20" /> : sub}
            </p>
        </div>
    </div>
);

const Mini = ({ value, label, cls }) => (
    <div className={`rounded-xl px-3 py-2.5 text-center ${cls}`}>
        <p className="break-words text-lg font-semibold leading-tight">{value}</p>
        <p className="text-[11px] opacity-70">{label}</p>
    </div>
);

const Chip = ({ children, cls = "bg-slate-100 text-slate-600" }) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${cls}`}>{children}</span>
);

const NoData = ({ text = "No data for this date" }) => (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span className="text-slate-400">{text}</span>} />
);

const SectionTitle = ({ icon, children, extra }) => (
    <div className="mb-3 flex items-center justify-between px-1">
        <h3 className="flex items-center gap-2 text-[15px] font-semibold text-slate-800">
            <span className="text-amber-400">{icon}</span> {children}
        </h3>
        {extra}
    </div>
);

const ExpandButton = ({ onClick, disabled }) => (
    <Tooltip title="Expand chart">
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label="Expand chart"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition hover:bg-sky-50 hover:text-sky-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
            <ExpandAltOutlined />
        </button>
    </Tooltip>
);

/* simple progress bar (used by hygiene card, overview, rankings) */
const Bar = ({ pct, track = "bg-slate-100", fill = "bg-sky-300" }) => (
    <div className={`h-1.5 w-full overflow-hidden rounded-full ${track}`}>
        <div className={`h-full rounded-full transition-all ${fill}`} style={{ width: `${Math.min(100, Math.max(0, pct || 0))}%` }} />
    </div>
);

/* ------------------------------------------------------------------
 * PRESENCE (cashier / writer) - FULL DAY, 15 MINUTE CANDLES
 *
 * Every candle = one 15 minute slot made of 15 boxes (one per minute).
 * green = present, red = absent, grey = no data.
 * ------------------------------------------------------------------ */
const MIN = 60000;
const minuteKey = (v) => {
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : Math.floor(t / MIN) * MIN;
};

const LegendDot = ({ color, label }) => (
    <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
        <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: color }} />
        {label}
    </span>
);

const cellColor = (state) => (state === "P" ? C.present : state === "A" ? C.absent : C.empty);
const stateWord = (state) => (state === "P" ? "Present" : state === "A" ? "Absent" : "No data");

// one candle = 15 minute boxes, minute 0 at the bottom, minute 14 on top
const Candle = ({ slot, large, showLabel, showCount }) => {
    const cell = large ? "h-[15px]" : "h-[9px]";
    const pct = slot.known ? Math.round((slot.present / slot.known) * 100) : 0; // bar width only

    return (
        <div className="flex min-w-0 flex-1 flex-col items-center">
            {showCount && (
                <span className="mb-1 text-[10px] font-semibold text-slate-500">{slot.known ? `${slot.present}m` : "-"}</span>
            )}

            <div
                className={`flex w-full flex-col-reverse rounded-[5px] bg-slate-50 transition hover:bg-sky-50 ${
                    large ? "gap-[2px] p-[2px]" : "gap-[1px] p-[1px]"
                } ${slot.approx ? "opacity-60" : ""}`}
            >
                {slot.cells.map((c) => (
                    <div
                        key={c.ts}
                        title={`${fmtTime(c.ts)} - ${stateWord(c.state)}${slot.approx ? " (approx.)" : ""}`}
                        className={`${cell} w-full rounded-[2px] transition hover:brightness-90`}
                        style={{ background: cellColor(c.state) }}
                    />
                ))}
            </div>

            {/* mini presence bar */}
            <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: C.present }} />
            </div>

            {/* time label, centred under the candle, only on every Nth candle so nothing overlaps */}
            <div
                className="relative mt-1.5 h-3 w-full"
                title={`${slot.range} - ${minsText(slot.present)} present, ${minsText(slot.absent)} absent`}
            >
                {showLabel && (
                    <span className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] text-slate-400">
                        {slot.label}
                    </span>
                )}
            </div>
        </div>
    );
};

// fits the full width of the card, no scrollbar
const MinuteCandles = ({ slots, large = false }) => {
    if (!slots.length) return <NoData text="No presence data yet" />;

    const step = Math.max(1, Math.ceil(slots.length / (large ? 16 : 7)));
    const showCount = large && slots.length <= 30;

    return (
        <div className={`flex w-full items-end ${large ? "gap-[4px]" : "gap-[2px]"}`}>
            {slots.map((s, i) => (
                <Candle key={s.id} slot={s} large={large} showLabel={i % step === 0} showCount={showCount} />
            ))}
        </div>
    );
};


const toMin = (v) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(v || ""));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const inShift = (cur, s, e) => (s < e ? cur >= s && cur < e : cur >= s || cur < e);

const ShiftSummary = ({ stats }) => {
    if (!stats.length) return null;
    return (
        <div>
            <p className="mb-2 text-xs font-medium text-slate-500">Shift wise presence</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {stats.map((s) => (
                    <div key={s.key} className="rounded-2xl border border-slate-100 bg-slate-50/50 p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-slate-600">{s.label}</span>
                            <span className="text-[11px] text-slate-400">{s.range}</span>
                        </div>
                        {s.known === 0 ? (
                            <p className="py-2 text-[11px] text-slate-400">No data in this shift</p>
                        ) : (
                            <>
                                <div className="grid grid-cols-3 gap-2">
                                    <Mini value={minsText(s.present)} label="Present" cls="bg-emerald-50 text-emerald-600" />
                                    <Mini value={minsText(s.absent)} label="Absent" cls="bg-rose-50 text-rose-600" />
                                    <Mini value={minsText(s.longest)} label="Longest absence" cls="bg-amber-50 text-amber-600" />
                                </div>
                                <div className="mt-2.5">
                                    <Bar pct={s.pct} track="bg-rose-100" fill="bg-emerald-300" />
                                    <p className="mt-1 text-right text-[11px] text-slate-400">{s.pct}% present</p>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const PresenceCard = ({ title, icon, block, stateField, timeField, shifts }) => {
    const [expanded, setExpanded] = useState(false);

    const day = block?.day || {};
    const slots = day.slots || [];
    const slotMinutes = day.slotMinutes || 15;
    const presentMin = day.presentMinutes || 0;
    const absentMin = day.absentMinutes || 0;
    const total = day.totalChecks || 0;
    const now = isPresent(block?.latestStatus);

    const one = block?.oneHour || {};
    const mins = one.minuteData || [];

    // candles: trimmed to the active part of the day, one box per minute
    // every known minute of the day: timestamp -> "P" | "A"
    const minuteMap = useMemo(() => {
        const m = new Map();
        slots.forEach((s) => {
            const base = minuteKey(s.start);
            if (base === null) return;
            (s.cells || []).forEach((c, i) => {
                if (c === "P" || c === "A") m.set(base + i * MIN, c);
            });
        });
        return m;
    }, [slots]);

    const dayStart = slots.length ? minuteKey(slots[0].start) : null;
// candles re-cut so each one runs :01 to :15 (00:01-00:15, 00:16-00:30 ...)
const candles = useMemo(() => {
    if (!minuteMap.size || dayStart === null) return [];

    const SLOT = slotMinutes * MIN;
    const anchor = dayStart + MIN; // 11:01

    const keys = [...minuteMap.keys()].filter((k) => k >= anchor);
    if (!keys.length) return [];

    const maxKey = Math.max(...keys);
    const last = anchor + Math.floor((maxKey - anchor) / SLOT) * SLOT;

    const out = [];
    for (let t = anchor; t <= last; t += SLOT) {
        const cells = Array.from({ length: slotMinutes }, (_, i) => ({
            ts: t + i * MIN,
            state: minuteMap.get(t + i * MIN) || null,
        }));
        const present = cells.filter((c) => c.state === "P").length;
        const absent = cells.filter((c) => c.state === "A").length;

        out.push({
            id: String(t),
            cells,
            approx: false,
            present,
            absent,
            known: present + absent,
            label: fmtTime(t),
            range: `${fmtTime(t)} - ${fmtTime(t + (slotMinutes - 1) * MIN)}`,
        });
    }
    return out;
}, [minuteMap, slotMinutes, dayStart]);

// shift wise present / absent minutes
    const shiftStats = useMemo(() => {
        if (!shifts) return [];
        const DAY = 24 * 60 * MIN;
        const keys = [...minuteMap.keys()].sort((a, b) => a - b);

        return ["shift1", "shift2"]
            .map((key, idx) => {
                const sh = shifts[key];
                const s = toMin(sh?.start);
                const e = toMin(sh?.end);
                if (s === null || e === null) return null;

                let present = 0;
                let absent = 0;
                let longest = 0;
                let run = 0;
                let prev = null;

                keys.forEach((ts) => {
                    const cur = (ts % DAY) / MIN;
                    if (!inShift(cur, s, e)) return;
                    if (minuteMap.get(ts) === "P") {
                        present += 1;
                        run = 0;
                    } else {
                        absent += 1;
                        run = prev !== null && ts - prev === MIN ? run + 1 : 1;
                        if (run > longest) longest = run;
                    }
                    prev = ts;
                });

                const known = present + absent;
                return {
                    key,
                    label: `Shift ${idx + 1}`,
                    range: `${sh.start} - ${sh.end}`,
                    present,
                    absent,
                    longest,
                    known,
                    pct: known ? Math.round((present / known) * 100) : 0,
                };
            })
            .filter(Boolean);
    }, [minuteMap, shifts]);

    const pie = [
        { name: "Present", value: presentMin, color: C.present },
        { name: "Absent", value: absentMin, color: C.absent },
    ];

    const Legends = () => (
        <div className="flex flex-wrap items-center gap-4">
            <LegendDot color={C.present} label="Present minute" />
            <LegendDot color={C.absent} label="Absent minute" />
            <LegendDot color={C.empty} label="No data" />
        </div>
    );

    return (
        <Card
            title={title}
            icon={icon}
            extra={
                <Chip cls={now ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}>
                    <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${now ? "bg-emerald-400" : "bg-rose-400"}`} />
                    {block?.latestStatus ? words(block.latestStatus) : "No data"}
                </Chip>
            }
        >
            {total === 0 ? (
                <NoData />
            ) : (
                <div className="space-y-5">
                    {/* SUMMARY */}
                    <div className="grid grid-cols-1 items-center gap-4 sm:grid-cols-5">
                        <div className="relative mx-auto h-40 w-40 sm:col-span-2">
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={pie} dataKey="value" innerRadius={52} outerRadius={72} paddingAngle={3} stroke="none">
                                        {pie.map((p) => (
                                            <Cell key={p.name} fill={p.color} />
                                        ))}
                                    </Pie>
                                    <RTooltip {...tipStyle} formatter={(v) => minsText(v)} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-xl font-bold text-slate-800">{minsText(presentMin)}</span>
                                <span className="text-[11px] text-slate-400">present today</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 sm:col-span-3 lg:grid-cols-3">
                            <Mini value={minsText(presentMin)} label="Present today" cls="bg-emerald-50 text-emerald-600" />
                            <Mini value={minsText(absentMin)} label="Absent today" cls="bg-rose-50 text-rose-600" />
                            <Mini value={minsText(day.longestAbsentMinutes)} label="Longest absence" cls="bg-amber-50 text-amber-600" />
                            <Mini value={fmtTime(day.firstSeen)} label="First seen" cls="bg-sky-50 text-sky-600" />
                            <Mini value={fmtTime(day.lastSeen)} label="Last seen" cls="bg-violet-50 text-violet-600" />
                            <Mini value={day.absentStretches || 0} label="Absence periods" cls="bg-slate-50 text-slate-600" />
                        </div>
                    </div>

                    <ShiftSummary stats={shiftStats} />

                    {/* MINUTE CANDLES */}
                    <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-3 sm:p-4">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <p className="text-xs font-semibold text-slate-600">{slotMinutes} minute candles</p>
                                <p className="text-[11px] text-slate-400">
                                    Each box is one minute (bottom = first minute of the slot, e.g. 00:01)
                                </p>
                            </div>
                            <ExpandButton onClick={() => setExpanded(true)} disabled={candles.length === 0} />
                        </div>

                        <MinuteCandles slots={candles} />
                    </div>

                    {/* LAST HOUR, MINUTE BY MINUTE */}
                    {mins.length > 0 && (
                        <div>
                            <p className="mb-2 text-xs font-medium text-slate-500">Last hour, minute by minute</p>
                            <div className="flex h-6 gap-[2px] overflow-hidden rounded-lg">
                                {mins.map((m, i) => (
                                    <Tooltip key={i} title={`${fmtTime(m[timeField])} - ${words(m[stateField])}`}>
                                        <div
                                            className="h-full flex-1 transition-opacity hover:opacity-70"
                                            style={{ background: isPresent(m[stateField]) ? C.present : C.absent }}
                                        />
                                    </Tooltip>
                                ))}
                            </div>
                            <div className="mt-1 flex justify-between text-[11px] text-slate-400">
                                <span>{fmtTime(mins[0]?.[timeField])}</span>
                                <span>{fmtTime(mins[mins.length - 1]?.[timeField])}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* EXPANDED CHART MODAL */}
            <Modal
                open={expanded}
                onCancel={() => setExpanded(false)}
                footer={null}
                centered
                destroyOnClose
                width="min(1200px, 95vw)"
                title={
                    <span className="flex items-center gap-2 text-[15px] font-semibold text-slate-800">
                        <span className="text-sky-500">{icon}</span>
                        {title} - {slotMinutes} minute candles
                    </span>
                }
            >
                <div className="mb-4 flex flex-wrap items-center gap-4">
                    <Legends />
                    <span className="ml-auto text-[11px] text-slate-400">
                        {minsText(presentMin)} present / {minsText(absentMin)} absent
                    </span>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-4">
                    <MinuteCandles slots={candles} large />
                </div>
                <p className="mt-3 text-[11px] text-slate-400">
                    Hover any box to see the exact minute. Bottom box is the first minute of each 15 minute slot.
                </p>
            </Modal>
        </Card>
    );
};

/* ------------------------------------------------------------------
 * KABAB FRY - summary, hourly chart and recent sessions
 * ------------------------------------------------------------------ */
const KababCard = ({ kabab }) => {
    const rows = kabab?.data || [];

    const { stats, hourly, recent } = useMemo(() => {
        const hours = Array.from({ length: 24 }, (_, h) => ({
            hour: `${String(h).padStart(2, "0")}:00`,
            Sessions: 0,
            sum: 0,
        }));
        let sum = 0;
        let longest = 0;
        let absence = 0;

        rows.forEach((r) => {
            const dur = Number(r.durationSeconds) || 0;
            sum += dur;
            absence += Number(r.maxAbsenceSeconds) || 0;
            if (dur > longest) longest = dur;
            const t = r.startTime ? new Date(r.startTime) : null;
            if (t && !Number.isNaN(t.getTime())) {
                hours[t.getUTCHours()].Sessions += 1;
                hours[t.getUTCHours()].sum += dur;
            }
        });

        const used = hours.map((h, i) => (h.Sessions ? i : -1)).filter((i) => i >= 0);
        const trimmed = used.length
            ? hours
                  .slice(Math.max(used[0] - 1, 0), Math.min(used[used.length - 1] + 2, 24))
                  .map((h) => ({ ...h, "Avg seconds": h.Sessions ? Math.round(h.sum / h.Sessions) : 0 }))
            : [];

        return {
            stats: {
                total: kabab?.count || rows.length,
                avg: rows.length ? Math.round(sum / rows.length) : 0,
                longest,
                avgAbsence: rows.length ? Math.round(absence / rows.length) : 0,
            },
            hourly: trimmed,
            recent: rows.slice(0, 6),
        };
    }, [rows, kabab]);

    return (
        <Card
            title="Kebab fry"
            icon={<FireOutlined />}
            extra={
                <Chip cls="bg-amber-50 text-amber-600">
                    <FireOutlined className="mr-1.5" />
                    {stats.total} sessions
                </Chip>
            }
        >
            {rows.length === 0 ? (
                <NoData text="No kabab fry sessions on this date" />
            ) : (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
                    <div className="space-y-4 xl:col-span-3">
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            <Mini value={stats.total} label="Sessions" cls="bg-amber-50 text-amber-600" />
                            <Mini value={durText(stats.avg)} label="Avg duration" cls="bg-sky-50 text-sky-600" />
                            <Mini value={durText(stats.longest)} label="Longest" cls="bg-violet-50 text-violet-600" />
                        </div>

                        <p className="text-xs font-medium text-slate-500">Sessions per hour</p>
                        <div className="h-52">
                            <ResponsiveContainer>
                                <BarChart data={hourly} margin={{ left: -26, right: 4, top: 4 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                                    <RTooltip {...tipStyle} />
                                    <RBar dataKey="Sessions" fill={C.amber} radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="xl:col-span-2">
                        <p className="mb-2 text-xs font-medium text-slate-500">Recent sessions</p>
                        <ul className="space-y-2">
                            {recent.map((r) => (
                                <li
                                    key={r._id || r.postgresId}
                                    className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5"
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-700">
                                            {fmtTime(r.startTime)} - {fmtTime(r.endTime)}
                                        </p>
                                        <p className="truncate text-[11px] text-slate-400">
                                            {r.cameraIp}
                                            {r.channel ? ` - ch ${r.channel}` : ""}
                                        </p>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <p className="text-sm font-semibold text-amber-500">{durText(r.durationSeconds)}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </Card>
    );
};

/* ------------------------------------------------------------------
 * HYGIENE & SAFETY
 * violation counts only (gloves / hairnet / duster), same rules as the hygiene page
 * ------------------------------------------------------------------ */
const HygieneTile = ({ label, value, total, cls }) => (
    <div className={`rounded-2xl px-4 py-3 ${cls}`}>
        <p className="text-2xl font-bold leading-tight">{value}</p>
        <p className="text-xs font-medium">{label}</p>
        <p className="text-[11px] opacity-70">{total ? Math.round((value / total) * 100) : 0}% of violations</p>
    </div>
);

const HygieneCard = ({ hygiene }) => {
    const rows = hygiene?.data || [];
    const total = rows.length;

    const stats = useMemo(() => ({ ...hygCounts(rows), recent: rows.slice(0, 6) }), [rows]);

    const flagged = [
        { label: "Missing gloves", value: stats.gloves, fill: "bg-sky-300" },
        { label: "Missing hairnet", value: stats.hairnet, fill: "bg-orange-300" },
        { label: "Both missing", value: stats.both, fill: "bg-rose-300" },
        { label: "Duster detected", value: stats.duster, fill: "bg-amber-300" },
    ];

    return (
        <Card
            title="Hygiene & safety"
            icon={<SafetyCertificateOutlined />}
            extra={<Chip cls="bg-teal-50 text-teal-600">{total} violations</Chip>}
        >
            {total === 0 ? (
                <NoData text="No hygiene violations on this date" />
            ) : (
                <div className="space-y-6">
                    {/* alert counts */}
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <HygieneTile label="Total violations" value={total} total={total} cls="bg-teal-50 text-teal-600" />
                        <HygieneTile label="Gloves alerts" value={stats.gloves} total={total} cls="bg-sky-50 text-sky-600" />
                        <HygieneTile label="Hairnet alerts" value={stats.hairnet} total={total} cls="bg-orange-50 text-orange-500" />
                        <HygieneTile label="Duster alerts" value={stats.duster} total={total} cls="bg-amber-50 text-amber-600" />
                    </div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
                        {/* what is being flagged */}
                        <div className="xl:col-span-3">
                            <p className="mb-3 text-xs font-medium text-slate-500">What's being flagged</p>
                            <div className="space-y-3.5">
                                {flagged.map((f) => (
                                    <div key={f.label}>
                                        <div className="mb-1 flex justify-between text-xs">
                                            <span className="text-slate-600">{f.label}</span>
                                            <span className="font-medium text-slate-700">
                                                {f.value}
                                                <span className="ml-1.5 text-slate-400">
                                                    {total ? Math.round((f.value / total) * 100) : 0}%
                                                </span>
                                            </span>
                                        </div>
                                        <Bar pct={total ? (f.value / total) * 100 : 0} fill={f.fill} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* recent alerts */}
                        <div className="xl:col-span-2">
                            <p className="mb-2 text-xs font-medium text-slate-500">Recent violations</p>
                            <ul className="space-y-2">
                                {stats.recent.map((r) => {
                                    const img =
                                        typeof r.imagePath === "string" && /^(https?:\/\/|\/|data:image\/)/i.test(r.imagePath)
                                            ? r.imagePath
                                            : pickImage(r);

                                    return (
                                        <li
                                            key={r._id || r.postgresId}
                                            className="flex items-center gap-3 rounded-xl bg-slate-50 px-3 py-2.5"
                                        >
                                            {img ? (
                                                <Image
                                                    src={img}
                                                    width={44}
                                                    height={44}
                                                    style={{ objectFit: "cover", borderRadius: 8 }}
                                                />
                                            ) : (
                                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white text-slate-300">
                                                    <SafetyCertificateOutlined />
                                                </span>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium text-rose-400">
                                                    {hygIssues(r).join(" + ")}
                                                </p>
                                                <p className="truncate text-[11px] text-slate-400">
                                                    {r.cameraIp}
                                                    {r.channel ? ` - ch ${r.channel}` : ""}
                                                </p>
                                            </div>
                                            <span className="shrink-0 text-xs text-slate-400">{fmtTime(r.postgresCreatedAt)}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

/* ------------------------------------------------------------------
 * ALERT ANALYTICS
 * ------------------------------------------------------------------ */
const AlertAnalytics = ({ d }) => {
    const { hourly, types, total } = useMemo(() => {
        const hours = Array.from({ length: 24 }, (_, h) => {
            const row = { hour: `${String(h).padStart(2, "0")}:00` };
            SOURCES.forEach((s) => (row[s.label] = 0));
            return row;
        });
        const typeMap = {};
        let total = 0;

        SOURCES.forEach((s) => {
            (d[s.key]?.data || []).forEach((r) => {
                total += 1;
                const t = r[s.timeField] ? new Date(r[s.timeField]) : null;
                if (t && !Number.isNaN(t.getTime())) hours[t.getUTCHours()][s.label] += 1;
                const type = r.alertType ? words(r.alertType) : `${s.label} event`;
                typeMap[type] = (typeMap[type] || 0) + 1;
            });
        });

        // trim empty leading/trailing hours for a tighter chart
        const used = hours.map((h, i) => (SOURCES.some((s) => h[s.label]) ? i : -1)).filter((i) => i >= 0);
        const trimmed = used.length ? hours.slice(used[0], used[used.length - 1] + 1) : [];

        const types = Object.entries(typeMap)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 6);

        return { hourly: trimmed, types, total };
    }, [d]);

    const palette = [C.sky, C.orange, C.violet, C.amber, C.present, C.absent];

    return (
        <Card title="Alert analytics" icon={<BellOutlined />} extra={<Chip cls="bg-rose-50 text-rose-600">{total} events</Chip>}>
            {total === 0 ? (
                <NoData text="No events on this date" />
            ) : (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                    <div className="lg:col-span-3">
                        <p className="mb-2 text-xs font-medium text-slate-500">Events by hour</p>
                        <div className="h-64">
                            <ResponsiveContainer>
                                <BarChart data={hourly} margin={{ left: -26, right: 4, top: 4 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                                    <RTooltip {...tipStyle} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    {SOURCES.map((s) => (
                                        <RBar key={s.key} dataKey={s.label} stackId="a" fill={s.color} />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <p className="mb-2 text-xs font-medium text-slate-500">Top event types</p>
                        <div className="space-y-3">
                            {types.map((t, i) => (
                                <div key={t.name}>
                                    <div className="mb-1 flex justify-between text-xs">
                                        <span className="truncate pr-2 text-slate-600">{t.name}</span>
                                        <span className="font-medium text-slate-700">{t.value}</span>
                                    </div>
                                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                                        <div
                                            className="h-full rounded-full"
                                            style={{ width: `${(t.value / types[0].value) * 100}%`, background: palette[i % palette.length] }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

/* ------------------------------------------------------------------
 * LATEST ACTIVITY (one newest event per source)
 * ------------------------------------------------------------------ */
const KababLatestBody = ({ row }) => {
    const done = String(row?.status || "").toLowerCase() === "completed";

    const [imageModal, setImageModal] = useState({
        open: false,
        title: "",
        url: "",
    });

    // Get the start or end image URL from the row
    const getSessionImage = (type) => {
        const keys =
            type === "start"
                ? [
                      "startImageUrl",
                      "startImage",
                      "startSnapshotUrl",
                      "startSnapshot",
                      "startFrameUrl",
                      "startFrame",
                      "startImagePath",
                  ]
                : ["endImageUrl", "endImage", "endSnapshotUrl", "endSnapshot", "endImagePath"];

        for (const key of keys) {
            const value = row?.[key];

            if (
                typeof value === "string" &&
                value.trim() &&
                (/^(https?:\/\/|\/)/i.test(value) || value.startsWith("data:image/"))
            ) {
                return value.trim();
            }
        }

        return null;
    };

    // Open image modal
    const openSessionImage = (type) => {
        const url = getSessionImage(type);

        if (!url) {
            message.info(`${type === "start" ? "Start" : "End"} image is not available`);
            return;
        }

        setImageModal({
            open: true,
            title: type === "start" ? "Start Image" : "End Image",
            url,
        });
    };

    // Started / Ended time card
    const TimeCard = ({ type, label, value }) => (
        <button
            type="button"
            onClick={() => openSessionImage(type)}
            className="group flex w-full items-center justify-between gap-2
                       rounded-xl border border-slate-200 bg-white
                       px-3 py-3 text-left transition-all duration-200
                       hover:border-amber-300 hover:bg-amber-50/50
                       focus:outline-none focus:ring-2 focus:ring-amber-200"
        >
            <div className="min-w-0">
                <p className="mb-1 text-xs font-medium text-slate-400">{label}</p>

                <p className="text-sm font-semibold text-slate-700">{fmtTimeSec(value)}</p>
            </div>

            {/* Image icon */}
            <span
                className="flex h-8 w-8 shrink-0 items-center justify-center
                           rounded-lg bg-slate-100 text-slate-500
                           transition-colors group-hover:bg-amber-100
                           group-hover:text-amber-700"
                title={`View ${label.toLowerCase()} image`}
                aria-label={`View ${label.toLowerCase()} image`}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="m21 15-5-5L5 21" />
                </svg>
            </span>
        </button>
    );

    return (
        <>
            {/* Main card body */}
            <div className="space-y-3 p-4">
                {/* Frying duration */}
                <div
                    className="flex items-center justify-between rounded-2xl
                               border border-amber-100
                               bg-gradient-to-r from-amber-50 to-orange-50
                               px-4 py-4"
                >
                    <div>
                        <p className="mb-1 text-xs font-medium text-amber-700/70">Frying time</p>

                        <p className="text-3xl font-bold tracking-tight text-amber-600">{durText(row.durationSeconds)}</p>
                    </div>

                    <Chip cls={done ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}>
                        {done && <CheckCircleFilled className="mr-1.5" />}

                        {words(row.status || "unknown")}
                    </Chip>
                </div>

                {/* Start and end times */}
                <div className="grid grid-cols-2 gap-3">
                    <TimeCard type="start" label="Started" value={row.startTime} />

                    <TimeCard type="end" label="Ended" value={row.endTime} />
                </div>
            </div>

            {/* Image modal */}
            <Modal
                open={imageModal.open}
                title={imageModal.title}
                footer={null}
                centered
                width={760}
                destroyOnHidden
                onCancel={() =>
                    setImageModal({
                        open: false,
                        title: "",
                        url: "",
                    })
                }
                styles={{
                    body: {
                        height: 480,
                        overflow: "hidden",
                        padding: "12px 0 0",
                    },
                }}
            >
                {/* Fixed-size image container */}
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        background: "#f8fafc",
                        borderRadius: "12px",
                    }}
                >
                    {imageModal.url && (
                        <img
                            key={imageModal.url}
                            src={imageModal.url}
                            alt={imageModal.title}
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                                message.error("Failed to load the image");
                            }}
                            style={{
                                display: "block",
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                            }}
                        />
                    )}
                </div>
            </Modal>
        </>
    );
};

const LatestCard = ({ source, row, loading }) => {
    const img = pickImage(row);
    const isKabab = source.key === "kabab";
    const when = row?.[source.timeField] || row?.alertTime || row?.timestamp;

    return (
        <div className="group flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-sky-200 hover:shadow-lg">

            {/* Header */}
            <div className="flex min-w-0 items-center justify-between gap-2 border-b border-slate-100 bg-white px-4 py-2">
                <div className="flex min-w-0 items-center gap-3">
                    <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-lg text-white shadow-sm"
                        style={{ background: source.color }}
                    >
                        {source.icon}
                    </span>

                    <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-800">
                            {source.label}
                        </p>
                    </div>
                </div>

                {row && !loading && (
                    <span className="flex shrink-0 items-center gap-1 text-[10px] font-medium text-slate-400">
                        <FieldTimeOutlined />
                        {fmtDateTime(when)}
                    </span>
                )}
            </div>

            {/* Loading */}
            {loading ? (
                <div className="flex-1 p-3">
                    <Skeleton active paragraph={{ rows: 3 }} title={false} />
                </div>
            ) : !row ? (
                <div className="flex min-h-[190px] flex-1 flex-col items-center justify-center gap-2 px-4 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-2xl text-slate-300">
                        {source.icon}
                    </span>

                    <div>
                        <p className="text-sm font-semibold text-slate-500">
                            No activity yet
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                            No recent {source.label.toLowerCase()} records found
                        </p>
                    </div>
                </div>
            ) : isKabab ? (
                <KababLatestBody row={row} />
            ) : (
                <div className="flex flex-1 flex-col p-3">

                    {/* Snapshot */}
                    <div className="relative mb-2 h-32 overflow-hidden rounded-xl bg-slate-50">
                        {img ? (
                            <Image
                                src={img}
                                alt={`${source.label} snapshot`}
                                className="!h-full !w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                wrapperClassName="block h-full w-full"
                            />
                        ) : (
                            <div className="flex h-full items-center justify-center text-3xl text-slate-300">
                                {source.icon}
                            </div>
                        )}
                    </div>

                    {/* Event information */}
                    <div className="flex-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                            Event type
                        </p>

                        <p className="mt-1 text-sm font-bold text-slate-700">
                            {row.alertType
                                ? words(row.alertType)
                                : `Latest ${source.label.toLowerCase()} event`}
                        </p>
                    </div>

                    {/* Camera information */}
                    {row.cameraIp && (
                        <div className="mt-2 flex items-center gap-2 border-t border-slate-100 pt-2 text-xs text-slate-400">
                            <VideoCameraOutlined />
                            <span className="truncate">
                                {row.cameraIp}
                                {row.channel ? ` · Channel ${row.channel}` : ""}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------
 * ONE FULL STORE DASHBOARD
 * Every block is gated by the API that feeds it, so each part appears
 * as soon as its own request is finished.
 * ------------------------------------------------------------------ */
const StoreDashboard = ({ d }) => {
    const rd = useReady();
    const cNow = isPresent(d.cashier?.latestStatus);
    const wNow = isPresent(d.writer?.latestStatus);

    return (
        <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                <Kpi
                    label="Cashier"
                    loading={!rd.presence}
                    value={d.cashier?.latestStatus ? words(d.cashier.latestStatus) : "-"}
                    sub={`${minsText(d.cashier?.day?.presentMinutes)} present today`}
                    icon={<UserOutlined />}
                    tint={cNow ? "bg-emerald-50 text-emerald-500" : "bg-rose-50 text-rose-500"}
                />
                <Kpi
                    label="Writer"
                    loading={!rd.presence}
                    value={d.writer?.latestStatus ? words(d.writer.latestStatus) : "-"}
                    sub={`${minsText(d.writer?.day?.presentMinutes)} present today`}
                    icon={<EditOutlined />}
                    tint={wNow ? "bg-emerald-50 text-emerald-500" : "bg-rose-50 text-rose-500"}
                />
                <Kpi
                    label="Alerts today"
                    loading={!rd.alerts}
                    value={alertTotal(d)}
                    sub="Cashier, writer, storeroom, hygiene"
                    icon={<BellOutlined />}
                    tint="bg-rose-50 text-rose-500"
                />
                {/* <Kpi
                    label="Hygiene alerts"
                    loading={!rd.alerts}
                    value={d.hygiene?.count || 0}
                    sub="Gloves, hairnet"
                    icon={<SafetyCertificateOutlined />}
                    tint="bg-teal-50 text-teal-500"
                /> */}
                <Kpi
                    label="Kebab fry sessions"
                    loading={!rd.kabab}
                    value={d.kabab?.count || 0}
                    sub="Recorded today"
                    icon={<FireOutlined />}
                    tint="bg-amber-50 text-amber-500"
                />
                <Kpi
                    label="Cameras"
                    loading={!rd.cameras}
                    value={d.cameras?.count || 0}
                    sub="Configured"
                    icon={<VideoCameraOutlined />}
                    tint="bg-sky-50 text-sky-500"
                />
            </div>

            <div className="mt-5">
    <SectionTitle icon={<ThunderboltOutlined />}>
        Latest Activity
    </SectionTitle>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SOURCES.map((s) => (
            <LatestCard
                key={s.key}
                source={s}
                loading={!rd[s.section]}
                row={d[s.key]?.latest || d[s.key]?.data?.[0]}
            />
        ))}
    </div>
</div>

            <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
                <Gate ready={rd.presence} title="Cashier presence" icon={<UserOutlined />} rows={8}>
                    <PresenceCard title="Cashier presence" icon={<UserOutlined />} block={d.cashier} stateField="cashierState" timeField="postgresCreatedAt" shifts={d.shiftTimings?.cashier} />
                </Gate>
                <Gate ready={rd.presence} title="Writer presence" icon={<EditOutlined />} rows={8}>
                    <PresenceCard title="Writer presence" icon={<EditOutlined />} block={d.writer} stateField="writerState" timeField="checkTime" shifts={d.shiftTimings?.writer} />
                </Gate>
            </div>

            <div className="mt-5">
                <Gate ready={rd.kabab} title="Kebab fry" icon={<FireOutlined />} rows={6}>
                    <KababCard kabab={d.kabab} />
                </Gate>
            </div>

            <div className="mt-5">
                <Gate ready={rd.alerts} title="Hygiene & safety" icon={<SafetyCertificateOutlined />} rows={6}>
                    <HygieneCard hygiene={d.hygiene} />
                </Gate>
            </div>

            <div className="mt-5">
                {/* analytics uses cashier / writer / storeroom / hygiene alerts and kebab fry events */}
                <Gate ready={rd.alerts && rd.kabab} title="Alert analytics" icon={<BellOutlined />} rows={6}>
                    <AlertAnalytics d={d} />
                </Gate>
            </div>
        </>
    );
};

/* ------------------------------------------------------------------
 * ALL STORES OVERVIEW
 * ------------------------------------------------------------------ */
const ALERT_HIGH = 10; // alerts per store before it is flagged for attention
const num = (v) => Number(v) || 0;

/* one place that turns a store's raw data into simple numbers */
const storeStats = (d) => {
    const cStatus = d.cashier?.latestStatus || null;
    const wStatus = d.writer?.latestStatus || null;
    const cNow = cStatus ? isPresent(cStatus) : null;
    const wNow = wStatus ? isPresent(wStatus) : null;

    // present time today in minutes (null = no data)
    const cMin = d.cashier?.day?.totalChecks ? num(d.cashier.day.presentMinutes) : null;
    const wMin = d.writer?.day?.totalChecks ? num(d.writer.day.presentMinutes) : null;

    // only used to draw the progress bars, never shown as text
    const cBar = d.cashier?.day?.totalChecks ? num(d.cashier.day.presencePct) : 0;
    const wBar = d.writer?.day?.totalChecks ? num(d.writer.day.presencePct) : 0;

    const alerts = alertTotal(d);

    const issues = [];
    if (cNow === false) issues.push("Cashier is absent");
    if (wNow === false) issues.push("Writer is absent");
    if (alerts >= ALERT_HIGH) issues.push(`${alerts} alerts today`);

    const mins = [cMin, wMin].filter((x) => x !== null);
    const presentAvgMin = mins.length ? Math.round(mins.reduce((a, b) => a + b, 0) / mins.length) : null;

    return {
        cStatus,
        wStatus,
        cNow,
        wNow,
        cMin,
        wMin,
        cBar,
        wBar,
        presentAvgMin,
        alerts,
        kabab: num(d.kabab?.count),
        storeRoom: num(d.storeRoom?.count),
        hygiene: num(d.hygiene?.count),
        cameras: num(d.cameras?.count),
        issues,
        ok: issues.length === 0,
    };
};

/* live cashier / writer numbers for ONE store, built from the "today" presence response.
 * This is independent of the date filter. */
const liveStats = (d) => {
    const cStatus = d?.cashier?.latestStatus || null;
    const wStatus = d?.writer?.latestStatus || null;

    return {
        cStatus,
        wStatus,
        cNow: cStatus ? isPresent(cStatus) : null,
        wNow: wStatus ? isPresent(wStatus) : null,
        cMin: d?.cashier?.day?.totalChecks ? num(d.cashier.day.presentMinutes) : null,
        wMin: d?.writer?.day?.totalChecks ? num(d.writer.day.presentMinutes) : null,
    };
};

/* ---------- hover breakdown shown when you move the cursor over an overview KPI card ----------
 * rows: [{ name, value, sub?, dot? }]
 *   dot = "ok" (green) | "bad" (red) | "warn" (amber) | undefined (grey)
 */
const DOT_CLS = {
    ok: "bg-emerald-400",
    bad: "bg-rose-400",
    warn: "bg-amber-400",
};
const VALUE_CLS = {
    ok: "text-emerald-600",
    bad: "text-rose-500",
    warn: "text-amber-600",
};

const KpiBreakdown = ({ title, rows }) => (
    <div className="min-w-[220px] max-w-[320px] p-3 text-slate-700">
        <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{title}</p>
        <ul className="max-h-72 space-y-0.5 overflow-y-auto">
            {rows.map((r) => (
                <li key={r.name} className="flex items-center justify-between gap-4 rounded-lg px-1 py-1.5 hover:bg-slate-50">
                    <span className="flex min-w-0 items-center gap-2">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLS[r.dot] || "bg-slate-300"}`} />
                        <span className="min-w-0">
                            <span className="block truncate text-[13px] font-medium text-slate-700">{r.name}</span>
                            {r.sub && <span className="block truncate text-[11px] text-slate-400">{r.sub}</span>}
                        </span>
                    </span>
                    <span className={`shrink-0 text-[13px] font-semibold ${VALUE_CLS[r.dot] || "text-slate-700"}`}>{r.value}</span>
                </li>
            ))}
        </ul>
    </div>
);

const OverviewKpi = ({ icon, label, value, sub, tone, loading, details, detailsTitle }) => {
    const hasDetails = !loading && Array.isArray(details) && details.length > 0;

    const card = (
        <div
            className={`flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm ${
                hasDetails ? "cursor-default transition hover:border-sky-200 hover:shadow-md" : ""
            }`}
        >
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg ${tone}`}>{icon}</span>
            <div className="min-w-0">
                <p className="truncate text-xs font-medium text-slate-500">{label}</p>
                <p className="truncate text-2xl font-semibold leading-tight text-slate-800">
                    {loading ? <Pulse className="h-6 w-14" /> : value}
                </p>
                <p className="truncate text-[11px] text-slate-400">{loading ? <Pulse className="h-3 w-20" /> : sub}</p>
            </div>
        </div>
    );

    if (!hasDetails) return card;

    return (
        <Tooltip
            color="#ffffff"
            placement="bottom"
            mouseEnterDelay={0.15}
            overlayStyle={{ maxWidth: 340 }}
            overlayInnerStyle={{ padding: 0, borderRadius: 16, boxShadow: "0 12px 32px -12px rgba(15,23,42,0.25)" }}
            title={<KpiBreakdown title={detailsTitle || label} rows={details} />}
        >
            {card}
        </Tooltip>
    );
};

const StaffRow = ({ icon, label, status, now, mins, bar, loading }) => {
    const tone =
        now === null
            ? "bg-slate-100 text-slate-400"
            : now
            ? "bg-emerald-50 text-emerald-600"
            : "bg-rose-50 text-rose-500";
    const fill = now === false ? "bg-rose-300" : "bg-emerald-300";

    return (
        <div>
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-[13px] font-medium text-slate-600">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-lg text-xs ${loading ? "bg-slate-100 text-slate-400" : tone}`}>{icon}</span>
                    {label}
                </span>
                {loading ? (
                    <Pulse className="h-5 w-24" />
                ) : (
                    <span className="flex items-center gap-2 text-xs">
                        <span className="text-slate-400">{mins === null ? "no data" : `${minsText(mins)} present`}</span>
                        <span className={`rounded-full px-2 py-0.5 font-medium ${tone}`}>{status ? words(status) : "No data"}</span>
                    </span>
                )}
            </div>
            <div className="mt-1.5">
                <Bar pct={loading ? 0 : bar || 0} fill={fill} />
            </div>
        </div>
    );
};

const Figure = ({ icon, value, label, loading }) => (
    <div className="rounded-xl bg-slate-50 px-2 py-2 text-center">
        <p className="text-base font-semibold leading-tight text-slate-700">
            {loading ? <Pulse className="h-4 w-6" /> : value}
        </p>
        <p className="mt-0.5 flex items-center justify-center gap-1 text-[10px] text-slate-400">
            {icon} {label}
        </p>
    </div>
);

/* ---------- 1. health banner ---------- */
const HealthBanner = ({ total, flagged, loading }) => {
    if (loading) {
        return (
            <div className="flex items-center gap-4 rounded-3xl border border-slate-200/70 bg-white p-4 sm:p-5">
                {/* Icon skeleton */}
                <div className="h-12 w-12 shrink-0 animate-pulse rounded-2xl bg-slate-100" />

                {/* Text skeleton */}
                <div className="min-w-0 flex-1">
                    <div className="mb-2 h-4 w-3/4 max-w-[280px] animate-pulse rounded-md bg-slate-100" />
                    <div className="h-3 w-full max-w-[420px] animate-pulse rounded-md bg-slate-100" />
                </div>
            </div>
        );
    }

    const good = flagged === 0;

    return (
        <div
            className={`flex items-center gap-4 rounded-3xl border p-4 sm:p-5 ${
                good ? "border-emerald-100 bg-emerald-50/70" : "border-amber-100 bg-amber-50/70"
            }`}
        >
            <span
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-2xl shadow-sm ${
                    good ? "text-emerald-400" : "text-amber-400"
                }`}
            >
                {good ? <CheckCircleFilled /> : <WarningOutlined />}
            </span>

            <div className="min-w-0">
                <p className="text-base font-semibold text-slate-800">
                    {good
                        ? `All ${total} ${total === 1 ? "store is" : "stores are"} running smoothly`
                        : `${flagged} of ${total} ${total === 1 ? "store needs" : "stores need"} your attention`}
                </p>

                <p className="text-sm text-slate-500">
                    {good
                        ? "Cashier and writer are present and alert counts are normal."
                        : "See the 'Needs attention' list below to find out what to check first."}
                </p>
            </div>
        </div>
    );
};

/* ---------- 2. needs attention ---------- */
const AttentionList = ({ rows, onOpen }) => {
    const flagged = rows.filter((r) => !r.s.ok);
    if (flagged.length === 0) return null;

    return (
        <Card title="Needs attention" icon={<WarningOutlined />} extra={<Chip cls="bg-amber-50 text-amber-600">{flagged.length} stores</Chip>}>
            <ul className="divide-y divide-slate-100">
                {flagged.map(({ store, s }) => (
                    <li key={store}>
                        <button
                            onClick={() => onOpen(store)}
                            className="flex w-full flex-wrap items-center justify-between gap-2 rounded-xl px-2 py-3 text-left transition hover:bg-slate-50"
                        >
                            <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-50 text-amber-500">
                                    <ShopOutlined />
                                </span>
                                {store}
                            </span>
                            <span className="flex flex-wrap items-center gap-1.5">
                                {s.issues.map((i) => (
                                    <Chip key={i} cls="bg-amber-50 text-amber-700">
                                        {i}
                                    </Chip>
                                ))}
                                <span className="ml-1 text-xs font-medium text-sky-500">Open &rarr;</span>
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </Card>
    );
};

/* ---------- 3. store cards ---------- */
const StoreSummaryCard = ({ name, s, onOpen }) => {
    const rd = useReady();
    const healthReady = rd.presence && rd.alerts; // "All good / Check" needs both

    return (
        <button
            onClick={onOpen}
            className="group flex flex-col rounded-3xl border border-slate-200/70 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
        >
            <div className="mb-4 flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-lg text-sky-500 ring-1 ring-sky-100">
                        <ShopOutlined />
                    </span>
                    <span className="truncate text-[15px] font-semibold text-slate-800">{name}</span>
                </span>
                {healthReady ? (
                    <Chip cls={s.ok ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}>
                        <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${s.ok ? "bg-emerald-400" : "bg-amber-400"}`} />
                        {s.ok ? "All good" : "Check"}
                    </Chip>
                ) : (
                    <Pulse className="h-6 w-16 rounded-full" />
                )}
            </div>

            <div className="space-y-3.5">
                <StaffRow icon={<UserOutlined />} label="Cashier" status={s.cStatus} now={s.cNow} mins={s.cMin} bar={s.cBar} loading={!rd.presence} />
                <StaffRow icon={<EditOutlined />} label="Writer" status={s.wStatus} now={s.wNow} mins={s.wMin} bar={s.wBar} loading={!rd.presence} />
            </div>

            <div className="mt-4 grid grid-cols-5 gap-1.5">
                <Figure icon={<BellOutlined />} value={s.alerts} label="Alerts" loading={!rd.alerts} />
                <Figure icon={<FireOutlined />} value={s.kabab} label="Kebab" loading={!rd.kabab} />
                <Figure icon={<InboxOutlined />} value={s.storeRoom} label="Storeroom" loading={!rd.alerts} />
                <Figure icon={<SafetyCertificateOutlined />} value={s.hygiene} label="Hygiene" loading={!rd.alerts} />
                <Figure icon={<VideoCameraOutlined />} value={s.cameras} label="Cameras" loading={!rd.cameras} />
            </div>

            <span className="mt-4 text-xs font-medium text-sky-500 transition group-hover:translate-x-0.5">
                Open full dashboard &rarr;
            </span>
        </button>
    );
};

/* ---------- 4. ranked lists (simple, no chart library) ---------- */
const RankList = ({ title, icon, rows, format = (v) => v, fill, empty = "No data yet", note, loading }) => {
    const max = Math.max(1, ...rows.map((r) => r.value || 0));
    return (
        <Card title={title} icon={icon} extra={note && <span className="text-[11px] text-slate-400">{note}</span>}>
            {loading ? (
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
            ) : rows.length === 0 ? (
                <NoData text={empty} />
            ) : (
                <ul className="space-y-3.5">
                    {rows.map((r, i) => (
                        <li key={r.name}>
                            <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                                <span className="flex min-w-0 items-center gap-2 text-slate-600">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-slate-100 text-[11px] font-semibold text-slate-500">
                                        {i + 1}
                                    </span>
                                    <span className="truncate">{r.name}</span>
                                </span>
                                <span className="shrink-0 font-semibold text-slate-700">{format(r.value)}</span>
                            </div>
                            <Bar pct={(r.value / max) * 100} fill={fill} />
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
};

/* ---------- 5. comparison table ---------- */
const Dot = ({ now }) => (
    <span
        className={`mr-1.5 inline-block h-2 w-2 rounded-full ${
            now === null ? "bg-slate-300" : now ? "bg-emerald-400" : "bg-rose-400"
        }`}
    />
);

const ComparisonTable = ({ rows, onOpen }) => {
    const rd = useReady();
    const th = "whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400";
    const td = "whitespace-nowrap px-3 py-3 text-sm text-slate-600";
    const time = (m) => (m === null ? "-" : minsText(m));
    // show a pulse until the API that feeds the cell has answered
    const L = (ready, node) => (ready ? node : <Pulse className="h-4 w-10" />);

    return (
        <Card title="Store comparison" icon={<AppstoreOutlined />} extra={<span className="text-xs text-slate-400">Click a row to open the store</span>}>
            <div className="-mx-1 overflow-x-auto">
                <table className="w-full min-w-[900px] border-collapse">
                    <thead>
                        <tr className="border-b border-slate-100">
                            <th className={th}>Store</th>
                            <th className={th}>Cashier</th>
                            <th className={th}>Writer</th>
                            <th className={th}>Cashier present</th>
                            <th className={th}>Writer present</th>
                            <th className={th}>Alerts</th>
                            <th className={th}>Kebab</th>
                            <th className={th}>Storeroom</th>
                            <th className={th}>Hygiene</th>
                            <th className={th}>Cameras</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {rows.map(({ store, s }) => (
                            <tr key={store} onClick={() => onOpen(store)} className="cursor-pointer transition hover:bg-sky-50/50">
                                <td className={`${td} font-semibold text-slate-800`}>{store}</td>
                                <td className={td}>
                                    {L(
                                        rd.presence,
                                        <>
                                            <Dot now={s.cNow} />
                                            {s.cStatus ? words(s.cStatus) : "No data"}
                                        </>
                                    )}
                                </td>
                                <td className={td}>
                                    {L(
                                        rd.presence,
                                        <>
                                            <Dot now={s.wNow} />
                                            {s.wStatus ? words(s.wStatus) : "No data"}
                                        </>
                                    )}
                                </td>
                                <td className={td}>{L(rd.presence, time(s.cMin))}</td>
                                <td className={td}>{L(rd.presence, time(s.wMin))}</td>
                                <td className={td}>
                                    {L(
                                        rd.alerts,
                                        <span className={s.alerts >= ALERT_HIGH ? "font-semibold text-amber-600" : ""}>{s.alerts}</span>
                                    )}
                                </td>
                                <td className={td}>{L(rd.kabab, s.kabab)}</td>
                                <td className={td}>{L(rd.alerts, s.storeRoom)}</td>
                                <td className={td}>{L(rd.alerts, s.hygiene)}</td>
                                <td className={td}>{L(rd.cameras, s.cameras)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

/* ---------- main overview ----------
 * live        = per store presence blocks for TODAY (never changes with the date filter)
 * liveLoading = true until the first live response has arrived
 */
const StoreOverview = ({ stores, onOpen, live = [], liveLoading = false }) => {
    const rd = useReady();
    const rows = useMemo(() => stores.map(({ store, data }) => ({ store, s: storeStats(data) })), [stores]);

    /* live cashier / writer rows: one per store shown in the overview */
    const liveRows = useMemo(
        () =>
            rows.map(({ store }) => ({
                store,
                l: liveStats(live.find((x) => x.store === store)?.data),
            })),
        [rows, live]
    );

    const totals = useMemo(() => {
        const withC = liveRows.filter((r) => r.l.cNow !== null);
        const withW = liveRows.filter((r) => r.l.wNow !== null);

        return {
            stores: rows.length,
            flagged: rows.filter((r) => !r.s.ok).length,
            alerts: rows.reduce((a, r) => a + r.s.alerts, 0),
            kabab: rows.reduce((a, r) => a + r.s.kabab, 0),
            hygiene: rows.reduce((a, r) => a + r.s.hygiene, 0),
            cameras: rows.reduce((a, r) => a + r.s.cameras, 0),
            // live numbers (today, right now)
            cPresent: withC.filter((r) => r.l.cNow).length,
            cTotal: withC.length,
            wPresent: withW.filter((r) => r.l.wNow).length,
            wTotal: withW.length,
        };
    }, [rows, liveRows]);

    const alertRank = [...rows].sort((a, b) => b.s.alerts - a.s.alerts).map((r) => ({ name: r.store, value: r.s.alerts }));
    const presenceRank = rows
        .filter((r) => r.s.presentAvgMin !== null)
        .sort((a, b) => b.s.presentAvgMin - a.s.presentAvgMin)
        .map((r) => ({ name: r.store, value: r.s.presentAvgMin }));

    const healthReady = rd.presence && rd.alerts; // absent staff + alert counts

    /* ---------- per store rows shown when hovering each KPI card ---------- */
    const storesDetails = useMemo(
        () =>
            rows.map(({ store, s }) => ({
                name: store,
                sub: s.ok ? undefined : s.issues.join(", "),
                dot: s.ok ? "ok" : "warn",
            })),
        [rows]
    );

    // live: always today's status, not the selected date
    const cashierDetails = useMemo(
        () =>
            liveRows.map(({ store, l }) => ({
                name: store,
                value: l.cStatus ? words(l.cStatus) : "No data",
                sub: l.cMin === null ? undefined : `${minsText(l.cMin)} present today`,
                dot: l.cNow === null ? undefined : l.cNow ? "ok" : "bad",
            })),
        [liveRows]
    );

    // live: always today's status, not the selected date
    const writerDetails = useMemo(
        () =>
            liveRows.map(({ store, l }) => ({
                name: store,
                value: l.wStatus ? words(l.wStatus) : "No data",
                sub: l.wMin === null ? undefined : `${minsText(l.wMin)} present today`,
                dot: l.wNow === null ? undefined : l.wNow ? "ok" : "bad",
            })),
        [liveRows]
    );

    const alertDetails = useMemo(
        () =>
            rows.map(({ store, s }) => ({
                name: store,
                value: s.alerts,
                sub:
                    [s.storeRoom && `${s.storeRoom} storeroom`, s.hygiene && `${s.hygiene} hygiene`]
                        .filter(Boolean)
                        .join(", ") || undefined,
                dot: s.alerts >= ALERT_HIGH ? "warn" : s.alerts > 0 ? undefined : "ok",
            })),
        [rows]
    );

    const hygieneDetails = useMemo(
        () =>
            rows.map(({ store, s }) => ({
                name: store,
                value: s.hygiene,
                dot: s.hygiene > 0 ? "warn" : "ok",
            })),
        [rows]
    );

    const kababDetails = useMemo(
        () =>
            rows.map(({ store, s }) => ({
                name: store,
                value: s.kabab,
                dot: s.kabab > 0 ? "ok" : undefined,
            })),
        [rows]
    );

    return (
        <div className="space-y-5">
            <HealthBanner total={totals.stores} flagged={totals.flagged} loading={!healthReady} />

            {/* TODAY AT A GLANCE (hover a card to see the per store numbers) */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                <OverviewKpi
                    icon={<ShopOutlined />}
                    label="Stores"
                    loading={!healthReady}
                    value={totals.stores}
                    sub="Being monitored"
                    tone="bg-sky-50 text-sky-500"
                    details={storesDetails}
                    detailsTitle="Store"
                />
                <OverviewKpi
                    icon={<UserOutlined />}
                    label="Cashiers present"
                    loading={liveLoading}
                    value={`${totals.cPresent}/${totals.cTotal}`}
                    sub="Right now (live)"
                    tone="bg-emerald-50 text-emerald-500"
                    details={cashierDetails}
                    detailsTitle="Cashier by store (live)"
                />
                <OverviewKpi
                    icon={<EditOutlined />}
                    label="Writers present"
                    loading={liveLoading}
                    value={`${totals.wPresent}/${totals.wTotal}`}
                    sub="Right now (live)"
                    tone="bg-violet-50 text-violet-500"
                    details={writerDetails}
                    detailsTitle="Writer by store (live)"
                />
                <OverviewKpi
                    icon={<BellOutlined />}
                    label="Alerts today"
                    loading={!rd.alerts}
                    value={totals.alerts}
                    sub="All stores"
                    tone="bg-rose-50 text-rose-400"
                    details={alertDetails}
                    detailsTitle="Alerts by store"
                />
                {/* <OverviewKpi
                    icon={<SafetyCertificateOutlined />}
                    label="Hygiene alerts"
                    loading={!rd.alerts}
                    value={totals.hygiene}
                    sub="All stores"
                    tone="bg-teal-50 text-teal-500"
                    details={hygieneDetails}
                    detailsTitle="Hygiene alerts by store"
                /> */}
                <OverviewKpi
                    icon={<FireOutlined />}
                    label="Kebab fry"
                    loading={!rd.kabab}
                    value={totals.kabab}
                    sub="Sessions today"
                    tone="bg-amber-50 text-amber-500"
                    details={kababDetails}
                    detailsTitle="Kebab fry sessions by store"
                />
            </div>

            {healthReady && <AttentionList rows={rows} onOpen={onOpen} />}

            {/* STORE CARDS */}
            <div>
                <div className="mb-3 flex items-center justify-between px-1">
                    <h3 className="flex items-center gap-2 text-[15px] font-semibold text-slate-800">
                        <span className="text-sky-400">
                            <ShopOutlined />
                        </span>
                        Your stores
                    </h3>
                    <span className="text-xs text-slate-400">Click a store for its full dashboard</span>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {rows.map(({ store, s }) => (
                        <StoreSummaryCard key={store} name={store} s={s} onOpen={() => onOpen(store)} />
                    ))}
                </div>
            </div>

            {/* RANKINGS */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <RankList title="Alerts by store" icon={<BellOutlined />} rows={alertRank} fill="bg-rose-300" loading={!rd.alerts} />
                <RankList
                    title="Staff present time today"
                    icon={<TeamOutlined />}
                    rows={presenceRank}
                    format={minsText}
                    fill="bg-emerald-300"
                    empty="No presence data"
                    note="Avg of cashier and writer"
                    loading={!rd.presence}
                />
            </div>

            <ComparisonTable rows={rows} onOpen={onOpen} />
        </div>
    );
};

/* ------------------------------------------------------------------
 * OVERVIEW SKELETON - shown only until the first store names arrive
 * (every component below has its own loading state, no page spinner)
 * ------------------------------------------------------------------ */
const OverviewSkeleton = () => (
    <div className="space-y-5">
        <HealthBanner loading />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <OverviewKpi icon={<ShopOutlined />} label="Stores" loading tone="bg-sky-50 text-sky-500" />
            <OverviewKpi icon={<UserOutlined />} label="Cashiers present" loading tone="bg-emerald-50 text-emerald-500" />
            <OverviewKpi icon={<EditOutlined />} label="Writers present" loading tone="bg-violet-50 text-violet-500" />
            <OverviewKpi icon={<BellOutlined />} label="Alerts today" loading tone="bg-rose-50 text-rose-400" />
            <OverviewKpi icon={<SafetyCertificateOutlined />} label="Hygiene alerts" loading tone="bg-teal-50 text-teal-500" />
            <OverviewKpi icon={<FireOutlined />} label="Kebab fry" loading tone="bg-amber-50 text-amber-500" />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((i) => (
                <CardSkeleton key={i} title="Store" icon={<ShopOutlined />} rows={5} />
            ))}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <CardSkeleton title="Alerts by store" icon={<BellOutlined />} rows={4} />
            <CardSkeleton title="Staff present time today" icon={<TeamOutlined />} rows={4} />
        </div>
    </div>
);

/* ------------------------------------------------------------------
 * PAGE
 * ------------------------------------------------------------------ */
const EmpireDashboard = () => {
    const storeName = localStorage.getItem(STORE_STORAGE_KEY) || ALL_STORES;
    const isAll = !storeName || storeName.trim().toLowerCase() === ALL_STORES.toLowerCase();

    const [date, setDate] = useState(todayStr());
    const [view, setView] = useState(OVERVIEW); // OVERVIEW or a store name
    const [updatedAt, setUpdatedAt] = useState(null);

    // one data state + one loading state for every API
    // data = block for the selected store, stores = per store blocks (All Stores only)
    const [cameras, setCameras] = useState({ data: {}, stores: [] });
    const [camerasLoading, setCamerasLoading] = useState(true);

    const [alerts, setAlerts] = useState({ data: {}, stores: [] });
    const [alertsLoading, setAlertsLoading] = useState(true);

    const [kabab, setKabab] = useState({ data: {}, stores: [] });
    const [kababLoading, setKababLoading] = useState(true);

    const [presence, setPresence] = useState({ data: {}, stores: [] });
    const [presenceLoading, setPresenceLoading] = useState(true);

    // LIVE presence: always TODAY, never follows the date filter.
    // feeds only the "Cashiers present" / "Writers present" cards in the overview.
    const [livePresence, setLivePresence] = useState({ data: {}, stores: [] });
    const [livePresenceLoading, setLivePresenceLoading] = useState(true);

    // request counters: an old response never overwrites a newer one (store / date change)
    const reqId = useRef({ cameras: 0, alerts: 0, kabab: 0, presence: 0, live: 0 });

    // shared error handling for every API call
    const showApiError = (err) => {
        if (err.response && err.response.data && err.response.data.msg) {
            message.warning(err.response.data.msg);
        } else if (err.response && err.response.data && err.response.data.userName) {
            message.warning(err.response.data.userName[0]);
        } else if (err.response && err.response.status === 401) {
            message.error("User not authorised");
        } else {
            message.error("Something went wrong");
        }
    };

    /* ---------------- API 1: cameras ---------------- */
    // showLoader = false for the silent 1 minute auto refresh (no skeleton flash)
    const fetchCameras = useCallback(
        async (showLoader = true) => {
            const id = ++reqId.current.cameras;
            try {
                if (showLoader) setCamerasLoading(true);
                const res = await axios.post(API.cameras, { store: storeName, date });
                if (id !== reqId.current.cameras) return;
                setCameras({ data: res.data?.data || {}, stores: res.data?.stores || [] });
                setUpdatedAt(new Date());
                setCamerasLoading(false);
            } catch (err) {
                if (id !== reqId.current.cameras) return;
                setCamerasLoading(false);
                showApiError(err);
            }
        },
        [storeName, date]
    );

    /* ---------------- API 2: alerts (cashier, writer, storeroom, hygiene) ---------------- */
    const fetchAlerts = useCallback(
        async (showLoader = true) => {
            const id = ++reqId.current.alerts;
            try {
                if (showLoader) setAlertsLoading(true);
                const res = await axios.post(API.alerts, { store: storeName, date });
                if (id !== reqId.current.alerts) return;
                setAlerts({ data: res.data?.data || {}, stores: res.data?.stores || [] });
                setUpdatedAt(new Date());
                setAlertsLoading(false);
            } catch (err) {
                if (id !== reqId.current.alerts) return;
                setAlertsLoading(false);
                showApiError(err);
            }
        },
        [storeName, date]
    );

    /* ---------------- API 3: kebab fry ---------------- */
    const fetchKabab = useCallback(
        async (showLoader = true) => {
            const id = ++reqId.current.kabab;
            try {
                if (showLoader) setKababLoading(true);
                const res = await axios.post(API.kabab, { store: storeName, date });
                if (id !== reqId.current.kabab) return;
                setKabab({ data: res.data?.data || {}, stores: res.data?.stores || [] });
                setUpdatedAt(new Date());
                setKababLoading(false);
            } catch (err) {
                if (id !== reqId.current.kabab) return;
                setKababLoading(false);
                showApiError(err);
            }
        },
        [storeName, date]
    );

    /* ---------------- API 4: presence (cashier + writer) ---------------- */
    const fetchPresence = useCallback(
        async (showLoader = true) => {
            const id = ++reqId.current.presence;
            try {
                if (showLoader) setPresenceLoading(true);
                const res = await axios.post(API.presence, { store: storeName, date });
                if (id !== reqId.current.presence) return;
                setPresence({ data: res.data?.data || {}, stores: res.data?.stores || [] });
                setUpdatedAt(new Date());
                setPresenceLoading(false);
            } catch (err) {
                if (id !== reqId.current.presence) return;
                setPresenceLoading(false);
                showApiError(err);
            }
        },
        [storeName, date]
    );

    /* ---------------- API 4b: LIVE presence (always today, ignores the date filter) ---------------- */
    // same endpoint as presence, but the date is always today's date.
    // it depends only on the store, so changing the date filter never re-fetches or changes it.
    const fetchLivePresence = useCallback(
        async (showLoader = true) => {
            const id = ++reqId.current.live;
            try {
                if (showLoader) setLivePresenceLoading(true);
                const res = await axios.post(API.presence, { store: storeName, date: todayStr() });
                if (id !== reqId.current.live) return;
                setLivePresence({ data: res.data?.data || {}, stores: res.data?.stores || [] });
                setLivePresenceLoading(false);
            } catch (err) {
                if (id !== reqId.current.live) return;
                setLivePresenceLoading(false);
                showApiError(err);
            }
        },
        [storeName]
    );

    /* ---------------- effects: first load + silent auto refresh every minute ---------------- */
    useEffect(() => {
        fetchCameras();
        const id = setInterval(() => fetchCameras(false), REFRESH_MS);
        return () => clearInterval(id);
    }, [fetchCameras]);

    useEffect(() => {
        fetchAlerts();
        const id = setInterval(() => fetchAlerts(false), REFRESH_MS);
        return () => clearInterval(id);
    }, [fetchAlerts]);

    useEffect(() => {
        fetchKabab();
        const id = setInterval(() => fetchKabab(false), REFRESH_MS);
        return () => clearInterval(id);
    }, [fetchKabab]);

    useEffect(() => {
        fetchPresence();
        const id = setInterval(() => fetchPresence(false), REFRESH_MS);
        return () => clearInterval(id);
    }, [fetchPresence]);

    // live presence: runs once per store, then every minute. NOT tied to the date filter.
    useEffect(() => {
        fetchLivePresence();
        const id = setInterval(() => fetchLivePresence(false), REFRESH_MS);
        return () => clearInterval(id);
    }, [fetchLivePresence]);

    const reloadAll = () => {
        fetchCameras();
        fetchAlerts();
        fetchKabab();
        fetchPresence();
        fetchLivePresence();
    };

    // which components can show their data (their own API is finished)
    const ready = useMemo(
        () => ({
            cameras: !camerasLoading,
            alerts: !alertsLoading,
            kabab: !kababLoading,
            presence: !presenceLoading,
        }),
        [camerasLoading, alertsLoading, kababLoading, presenceLoading]
    );

    const anyLoading = camerasLoading || alertsLoading || kababLoading || presenceLoading || livePresenceLoading;

    // single store / no stores: merge the blocks of every API into one payload
    const payload = useMemo(
        () => Object.assign({}, cameras.data, alerts.data, kabab.data, presence.data),
        [cameras, alerts, kabab, presence]
    );

    // All Stores: store names = union of the stores each API returned
    // (tabs and cards fill in as the APIs answer)
    const stores = useMemo(() => {
        const all = [cameras, alerts, kabab, presence];
        const names = new Set();
        all.forEach((sec) => sec.stores.forEach((x) => names.add(x.store)));

        return [...names]
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({
                store: name,
                data: Object.assign({}, ...all.map((sec) => sec.stores.find((x) => x.store === name)?.data || {})),
            }));
    }, [cameras, alerts, kabab, presence]);

    // if the selected store tab disappears (e.g. date change), go back to overview
    useEffect(() => {
        if (view !== OVERVIEW && !stores.some((s) => s.store === view)) setView(OVERVIEW);
    }, [stores, view]);

    const showStores = isAll && stores.length > 0;
    const activeStore = showStores ? stores.find((s) => s.store === view) : null;

    // All Stores, nothing answered yet: skeleton cards instead of a page spinner
    const showOverviewSkeleton = isAll && stores.length === 0 && anyLoading;

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50/60 via-slate-50 to-white p-3 sm:p-6">
            <div className="mx-auto max-w-7xl space-y-5">
                {/* HEADER */}
                <header className="flex flex-col gap-4 rounded-3xl border border-sky-100 bg-gradient-to-r from-sky-50 via-white to-indigo-50 p-5 text-slate-800 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div>
                        <h1 className="text-xl font-semibold text-slate-800 sm:text-2xl">Empire Analytics</h1>
                        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
                            <ShopOutlined /> {isAll ? ALL_STORES : storeName}
                            {activeStore && <span className="font-medium text-sky-500"> / {activeStore.store}</span>}
                        </p>
                        {updatedAt && (
                            <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                                Live, updated {updatedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm text-slate-600 ring-1 ring-slate-200">
                            <CalendarOutlined />
                            <input
                                type="date"
                                value={date}
                                max={todayStr()}
                                onChange={(e) => e.target.value && setDate(e.target.value)}
                                className="bg-transparent outline-none [color-scheme:light]"
                            />
                        </label>
                        <button
                            onClick={reloadAll}
                            disabled={anyLoading}
                            className="flex items-center gap-2 rounded-xl bg-sky-400 px-4 py-2 text-sm font-medium text-white transition hover:bg-sky-500 disabled:opacity-60"
                        >
                            <ReloadOutlined spin={anyLoading} /> Refresh
                        </button>
                    </div>
                </header>

                {/* STORE TABS - only in All Stores mode */}
                {showStores && (
                    <nav className="flex gap-1.5 overflow-x-auto rounded-2xl border border-slate-200/70 bg-white p-1.5 shadow-sm">
                        {[
                            { key: OVERVIEW, label: "Overview", icon: <AppstoreOutlined /> },
                            ...stores.map((s) => ({ key: s.store, label: s.store, icon: <ShopOutlined />, n: alertTotal(s.data) })),
                        ].map((t) => {
                            const on = view === t.key;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setView(t.key)}
                                    className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                                        on ? "bg-sky-50 text-sky-600 shadow-sm ring-1 ring-sky-200" : "text-slate-500 hover:bg-slate-50"
                                    }`}
                                >
                                    {t.icon} {t.label}
                                    {t.n > 0 && (
                                        <span className="rounded-full bg-rose-50 px-1.5 text-[11px] text-rose-600">{t.n}</span>
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                )}

                <SectionsCtx.Provider value={ready}>
                    {showOverviewSkeleton ? (
                        <OverviewSkeleton />
                    ) : (
                        <>
                            {showStores && view === OVERVIEW && (
                                <StoreOverview
                                    stores={stores}
                                    onOpen={setView}
                                    live={livePresence.stores}
                                    liveLoading={livePresenceLoading}
                                />
                            )}
                            {showStores && activeStore && <StoreDashboard d={activeStore.data} />}
                            {!showStores && <StoreDashboard d={payload} />}
                        </>
                    )}
                </SectionsCtx.Provider>
            </div>
        </div>
    );
};

export default EmpireDashboard;